// MARK://dhk8/storefront/pages/ConsultaPage
// Stream 3 Fase 1 Super Plan — consulta pública por código.
//
// URL: /consulta/:tenantSlug (slug humano, no UUID).
// El comensal escribe su código (ej. "A47") y vemos primero la tabla de
// reservaciones; si no aparece allí, fallback a waitlist. Ambos endpoints
// son rate-limited en el backend (30 lookups/IP/min).
//
// Sin auth. Sin escrituras. Solo lectura.

import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import BrandHeader from '../components/BrandHeader.js'
import { useTenantBranding } from '../lib/branding.js'

const API_BASE =
  (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env['VITE_API_BASE'] ??
  '/v1'

interface ReservationLookup {
  kind: 'reservation'
  name: string
  partySize: number
  reservedAt: string
  status: 'CONFIRMED' | 'ARRIVED' | 'SEATED' | 'CANCELLED' | 'NO_SHOW'
  code: string
}

interface WaitlistLookup {
  kind: 'waitlist'
  name: string
  partySize: number
  joinedAt: string
  status: 'WAITING' | 'NOTIFIED' | 'SEATED' | 'LEFT' | 'EXPIRED'
  position: number
  peopleAhead: number
  estimatedWaitMin: number | null
  code: string
}

type Lookup = ReservationLookup | WaitlistLookup

interface ConsultaTenantInfo {
  tenantId: string | null
  displayName: string
}

async function fetchTenantId(slug: string): Promise<ConsultaTenantInfo> {
  // Si el slug es inválido, devolvemos null sin pegar al backend.
  if (!/^[a-z0-9-]{2,80}$/.test(slug)) {
    return { tenantId: null, displayName: 'NintaiGO' }
  }
  try {
    const res = await fetch(
      `${API_BASE}/dhk8-platform-tenants/v1/tenants/by-slug/${encodeURIComponent(slug)}`,
      { headers: { accept: 'application/json' } },
    )
    if (!res.ok) return { tenantId: null, displayName: 'NintaiGO' }
    const body = (await res.json()) as
      | { ok: true; data: { tenantId: string; slug: string; name?: string | null } }
      | { ok: false }
    if (!('ok' in body) || !body.ok) return { tenantId: null, displayName: 'NintaiGO' }
    return {
      tenantId: body.data.tenantId,
      displayName: body.data.name ?? 'NintaiGO',
    }
  } catch {
    return { tenantId: null, displayName: 'NintaiGO' }
  }
}

/** Consulta la FILA por codigo. `null` si no hay turno con ese codigo. */
async function buscarEnFila(tenantSlug: string, code: string): Promise<Lookup | null> {
  try {
    const w = await fetch(
      `${API_BASE}/dhk8-pos-floor/v1/floor/public/waitlist/by-code/${encodeURIComponent(tenantSlug)}/${encodeURIComponent(code)}`,
      { headers: { accept: 'application/json' } },
    )
    if (w.ok) {
      const body = (await w.json()) as
        | { ok: true; data: Omit<WaitlistLookup, 'kind'> }
        | { ok: false }
      if ('ok' in body && body.ok) {
        return { kind: 'waitlist', ...body.data }
      }
    }
  } catch {
    // red intermitente
  }
  return null
}

async function lookupCode(
  tenantSlug: string,
  code: string,
  preferirFila = false,
): Promise<Lookup | null> {
  // Si el comensal viene de la fila, se consulta la fila PRIMERO: los codigos
  // de reserva y de fila comparten formato (A1, A2...) y antes ganaba siempre
  // la reserva, mostrandole el turno de alguien mas.
  if (preferirFila) {
    const f = await buscarEnFila(tenantSlug, code)
    if (f !== null) return f
  }
  // 1) Tabla reservations.
  try {
    const r = await fetch(
      `${API_BASE}/dhk8-pos-floor/v1/floor/public/reservations/by-code/${encodeURIComponent(tenantSlug)}/${encodeURIComponent(code)}`,
      { headers: { accept: 'application/json' } },
    )
    if (r.ok) {
      const body = (await r.json()) as
        | { ok: true; data: Omit<ReservationLookup, 'kind'> }
        | { ok: false }
      if ('ok' in body && body.ok) {
        return { kind: 'reservation', ...body.data }
      }
    }
  } catch {
    // continúa con waitlist
  }
  // 2) Fallback waitlist.
  const f = await buscarEnFila(tenantSlug, code)
  if (f !== null) return f
  return null
}

const RESERVATION_STATUS_LABEL: Record<ReservationLookup['status'], string> = {
  CONFIRMED: 'Confirmada',
  ARRIVED: 'Ya llegaste — esperando mesa',
  SEATED: 'Sentado',
  CANCELLED: 'Cancelada',
  NO_SHOW: 'No-show',
}

const WAITLIST_STATUS_LABEL: Record<WaitlistLookup['status'], string> = {
  WAITING: 'En espera',
  NOTIFIED: '¡Tu mesa está lista!',
  SEATED: 'Ya estás sentado',
  LEFT: 'Saliste de la fila',
  EXPIRED: 'Espera vencida',
}

export default function ConsultaPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const slug = (tenantSlug ?? '').trim().toLowerCase()
  const [tenantInfo, setTenantInfo] = useState<ConsultaTenantInfo>({
    tenantId: null,
    displayName: 'NintaiGO',
  })
  // Branding solo lo pintamos si resolvemos el tenantId (necesita UUID).
  const branding = useTenantBranding(tenantInfo.tenantId)

  // El enlace de la fila trae `?tipo=fila&code=A1`: se precarga el codigo y se
  // consulta la FILA primero (los codigos de reserva y fila comparten formato).
  const [params] = useSearchParams()
  const preferirFila = params.get('tipo') === 'fila'
  const [code, setCode] = useState(() => (params.get('code') ?? '').toUpperCase())
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<Lookup | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    void fetchTenantId(slug).then((info) => {
      if (alive) setTenantInfo(info)
    })
    return () => {
      alive = false
    }
  }, [slug])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    setResult(null)
    setNotFound(false)
    const normalized = code.trim().toUpperCase()
    if (!/^[A-Z][0-9]{1,5}$/.test(normalized)) {
      setErr('Formato inválido. Tu código es una letra + número (ej. A47).')
      return
    }
    if (!slug) {
      setErr('URL inválida.')
      return
    }
    setBusy(true)
    try {
      const found = await lookupCode(slug, normalized, preferirFila)
      if (found) setResult(found)
      else setNotFound(true)
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error al consultar')
    } finally {
      setBusy(false)
    }
  }

  const hasCustomBranding =
    branding.displayName && branding.displayName.trim().toLowerCase() !== 'nintaigo'

  return (
    <div className="sf-page">
      <BrandHeader branding={branding} />
      <div className="sf-form-page">
        <form className="sf-card" onSubmit={(e) => void handleSubmit(e)}>
          <h1 className="sf-card__title">Consultar mi reserva</h1>
          <p className="sf-card__lede">
            {hasCustomBranding ? (
              <>
                Consulta tu reserva o tu lugar en la fila en{' '}
                <strong>{branding.displayName}</strong>.
              </>
            ) : (
              <>Ingresa tu código para ver el estado de tu reserva o turno.</>
            )}
          </p>
          <label className="sf-field">
            <span className="sf-field__label">Tu código (ej. A47)</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              maxLength={6}
              autoFocus
              autoCapitalize="characters"
              spellCheck={false}
              className="sf-input sf-consulta-input"
              placeholder="A47"
            />
          </label>
          {err && <div className="sf-error">{err}</div>}
          <button type="submit" disabled={busy || !code.trim()} className="sf-btn-primary">
            {busy ? 'Consultando…' : 'Consultar'}
          </button>

          {result && result.kind === 'reservation' ? (
            <ResultReservation r={result} />
          ) : null}
          {result && result.kind === 'waitlist' ? (
            <ResultWaitlist w={result} />
          ) : null}
          {notFound ? (
            <div className="sf-error" role="alert">
              No encontramos esa reserva ni un turno con ese código. Verifica
              con el restaurante.
            </div>
          ) : null}
        </form>
      </div>
    </div>
  )
}

function ResultReservation({ r }: { r: ReservationLookup }) {
  const formatted = new Date(r.reservedAt).toLocaleString('es-MX', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  })
  return (
    <div className="sf-code-block" data-testid="result-reservation">
      <span className="sf-code-block__label">Tu reserva — código {r.code}</span>
      <span className="sf-code-block__value" style={{ fontSize: 'var(--text-headline)' }}>
        {r.name}
      </span>
      <p className="sf-card__highlight" style={{ margin: 0 }}>{formatted}</p>
      <p className="sf-code-block__hint">
        {r.partySize} {r.partySize === 1 ? 'persona' : 'personas'} ·{' '}
        <strong>{RESERVATION_STATUS_LABEL[r.status]}</strong>
      </p>
    </div>
  )
}

function ResultWaitlist({ w }: { w: WaitlistLookup }) {
  const formatted = new Date(w.joinedAt).toLocaleString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
  })
  return (
    <div className="sf-code-block" data-testid="result-waitlist">
      <span className="sf-code-block__label">Tu turno — código {w.code}</span>
      <span className="sf-code-block__value" style={{ fontSize: 'var(--text-headline)' }}>
        {w.name}
      </span>
      <p className="sf-card__highlight" style={{ margin: 0 }}>
        {w.status === 'NOTIFIED'
          ? '¡Tu mesa está lista!'
          : `${w.peopleAhead} ${w.peopleAhead === 1 ? 'persona adelante' : 'personas adelante'}`}
      </p>
      <p className="sf-code-block__hint">
        Anotado a las {formatted} · {w.partySize}{' '}
        {w.partySize === 1 ? 'persona' : 'personas'} ·{' '}
        <strong>{WAITLIST_STATUS_LABEL[w.status]}</strong>
        {w.estimatedWaitMin != null ? (
          <>
            {' '}
            · Espera estimada: ~{w.estimatedWaitMin} min
          </>
        ) : null}
      </p>
    </div>
  )
}
